import { ActionIcon, Button, Group, NumberInput, Space, Stack, Title } from "@mantine/core";
import { IconCircleFilled, IconX } from "@tabler/icons-react";
import { Fragment, useEffect } from "react";
import { useComposeFormContext } from "shared/components/message/compose/context";
import { useFormState } from "shared/hooks/useFormState";
import { arr } from "shared/utils/array";
import { useAside } from "shared/utils/aside";
import { MessageSeverity } from "src/gql/graphql";

const MAX_ESCALATIONS = 2;

export const EscalationEditor = (props: { mode?: "widget" | "form" }) => {
    const aside = useAside();
    const form = useComposeFormContext();
    const [severity] = useFormState(form, "severity");
    const [acknowledgable, setAcknowledgable] = useFormState(form, "acknowledgable");
    const [escalations, setEscalations] = useFormState(form, "escalations");

    function handleCreateEscalation() {
        setEscalations(escalations.concat([{ delayMin: 5 }]));
    }

    useEffect(() => {
        if (severity != MessageSeverity.Urgent) {
            setAcknowledgable(false);
            setEscalations([]);
            aside.dismiss();
        }
    }, [severity]);

    useEffect(() => {
        if (!acknowledgable) setEscalations([]);
    }, [acknowledgable]);

    return (
        <Stack pos={"relative"}>
            {(props.mode != "widget" || escalations.length > 0) && (
                <Stack style={{ borderLeft: "dashed 1px" }} pl="md">
                    <Group>
                        <IconCircleFilled size={10} style={{ position: "absolute", left: 0, transform: "translate(-45%, 0px)" }} />
                        <Title order={3}>Message Sent</Title>
                    </Group>

                    <Space h={"xl"} />

                    {escalations.map((each, i) => (
                        <Fragment key={`escalation-${i}`}>
                            <Group>
                                <IconCircleFilled size={10} style={{ position: "absolute", left: 0, transform: "translate(-45%, 0px)" }} />
                                <Title order={3}>Escalation {i + 1}</Title>
                            </Group>

                            <Group align="flex-end" gap={"xs"}>
                                <NumberInput
                                    label="Delay"
                                    min={5}
                                    suffix=" min"
                                    value={each.delayMin}
                                    onChange={(v) =>
                                        arr(escalations)
                                            .update({ to: { delayMin: +v }, where: (_, idx) => idx == i })
                                            .take((it) => setEscalations(it))
                                    }
                                />
                                <ActionIcon
                                    variant="transparent"
                                    size={"input-sm"}
                                    onClick={() => setEscalations(escalations.toSpliced(i, 1))}
                                    aria-label="Remove escalation"
                                >
                                    <IconX size={16} />
                                </ActionIcon>
                            </Group>

                            <Space h={"xl"} />
                        </Fragment>
                    ))}

                    <Group>
                        <IconCircleFilled size={10} style={{ position: "absolute", left: 0, transform: "translate(-45%, 0px)" }} />
                        {props.mode != "widget" && (
                            <Button
                                variant="default"
                                disabled={!acknowledgable || escalations.length >= MAX_ESCALATIONS}
                                onClick={handleCreateEscalation}
                            >
                                Add Escalation
                            </Button>
                        )}
                    </Group>
                </Stack>
            )}
        </Stack>
    );
};
